import icn_linebar_chart from 'assets/images/icons/icn_linebar_chart.png';
import icn_info_solid from 'assets/images/icons/icn_Info_solid.svg';

import React from 'react';
import { Box, Typography, Divider, Tooltip, tooltipClasses } from '@mui/material';
import { ReactComponent as Clock } from 'assets/images/icons/icn_display_clock.svg';
import { styled, useTheme } from '@mui/material/styles';

import SatisfactionBarChart from 'components/SatisfactionBarChart';
import CircularLoader from 'components/CircularLoader';

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#4D4D4D',
    color: '#FFFFFF',
    maxWidth: 600,
    fontSize: theme.typography.pxToRem(12),
    padding: '16px 20px',
    borderRadius: '8px',
    lineHeight: '1.5',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
  },
}));

const ActionList = ({ items }) => (
  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
    {items.map((item, index) => (
      <Box component="li" key={index} sx={{ mb: 0.5 }}>
        {item}
      </Box>
    ))}
  </Box>
);

const TooltipContent = () => (
  <Box>
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Good — Short Sessions (&lt;1 min) ≤ 10%</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px' }}>
        Guest session durations are within the normal range. Short sessions remain rare and the guest experience is stable.
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Warning — 10% &lt; Short Sessions ≤ 25%</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
        Short sessions (&lt;1 minute) are increasing, which may indicate congestion at peak hours or degraded experience in specific areas.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Recommended Actions:</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '12px' }}>
        <li>Review signal quality and load on the Top APs and SSIDs.</li>
        <li>Simplify the captive portal flow if it is causing users to abandon login.</li>
      </Box>
    </Box>

    <Box>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Critical — Short Sessions &gt; 25%</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
        Many guest sessions end right after connecting, concentrated on specific APs or SSIDs.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Recommended Actions:</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '12px' }}>
        <li>Identify the high-impact APs or SSIDs and investigate coverage, interference, and uplink quality.</li>
        <li>Apply short-term mitigation such as switching to backup APs or simplifying authentication.</li>
      </Box>
    </Box>
  </Box>
);

const MetricRow = ({ label, percentage }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
    <Typography sx={{ fontSize: '13px', color: '#1A202C', fontWeight: 'bold', mr: 1 }}>
      {label}:
    </Typography>
    <Typography sx={{ fontSize: '13px', color: '#4C6EF5', fontWeight: 'bold' }}>
      {percentage}%
    </Typography>
  </Box>
);

const CaptivePortalAccessSatisfactionScore = ({ chartData, isEmpty, onClick, loading = false }) => {
  const theme = useTheme();
  const score = chartData
    ? (chartData.sessions > 0 && chartData.series?.[0]?.[1] !== undefined
      ? 100 - Math.round((chartData.series[0][1] / chartData.sessions) * 100)
      : 100)
    : 0;
  const labels = ['< 1 Min', '1-10 Mins', '10-60 Mins', '> 60 Mins'];
  const handleOpenDrawer = !isEmpty ? onClick : undefined;

  const getStatus = (val) => {
    if (val >= 90) return { label: 'Good', color: '#4CAF50' };
    if (val >= 75) return { label: 'Warning', color: '#FF9800' };
    return { label: 'Critical', color: '#F44336' };
  };

  const status = getStatus(score);

  return (
    <Box
      sx={{
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        p: 2,
        bgcolor: 'white',
        mb: 3,
        cursor: isEmpty ? 'default' : 'pointer',
        pointerEvents: isEmpty ? 'none' : 'auto',
        '&:hover': handleOpenDrawer ? {
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        } : {}
      }}
      onClick={handleOpenDrawer}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src={icn_linebar_chart}
            sx={{ width: '18px', height: '18px', mr: 1 }}
          />
          <Typography sx={{ fontWeight: 'bold', color: '#172664', fontSize: '15px', mr: 1 }}>
            Captive Portal Satisfaction
          </Typography>
          <HtmlTooltip title={<TooltipContent />} placement="auto">
            <Box component="img" src={icn_info_solid} sx={{ width: '16px', height: '16px', cursor: 'pointer' }} />
          </HtmlTooltip>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: theme.palette.primary.main,
          }}
        >
          <Clock />
          <Typography
            sx={{
              marginLeft: '5px',
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[2],
            }}
          >
            Last 24 Hours
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularLoader />
        </Box>
      ) : isEmpty ? (
        <Box sx={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography
            sx={{
              color: theme.palette.primary.greyThird,
              fontSize: theme.typography.customFontSize[2],
            }}>
            No data available
          </Typography>
        </Box>
      ) : (
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' }
        }}>
          <Box sx={{
            width: { xs: '100%', md: '35%' },
            pr: { xs: 0, md: 4 },
            mb: { xs: 3, md: 0 }
          }}>
            <Box sx={{ display: 'flex', mb: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: '#718096', fontWeight: 'bold', mb: 1, display: 'block', fontSize: '13px' }}>
                  Status
                </Typography>
                <Typography sx={{ fontSize: '35px', fontWeight: 'bold', color: status.color, lineHeight: 1.2 }}>
                  {status.label}
                </Typography>
              </Box>
              {/* <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: '#718096', fontWeight: 'bold', mb: 1, display: 'block', fontSize: '13px' }}>
                  Captive Portal Access Score
                </Typography>
                <Typography sx={{ fontSize: '35px', fontWeight: 'bold', color: '#172664', lineHeight: 1.2 }}>
                  {score}
                </Typography>
              </Box> */}
            </Box>

            <Divider sx={{ mb: 3, width: '100%' }} />

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Typography sx={{ fontSize: '13px', color: '#1A202C', fontWeight: 'bold', mr: 1 }}>
                  Sessions:
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#4C6EF5', fontWeight: 'bold' }}>
                  {chartData.sessions.toLocaleString()}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
                flexWrap: 'wrap',
                gap: { xs: 2, md: 0 }
              }}>
                {chartData.series.map((item, index) => {
                  const percentage = chartData.sessions > 0
                    ? Math.round((item[1] / chartData.sessions) * 100)
                    : 0;
                  return (
                    <MetricRow
                      key={index}
                      label={labels[index]}
                      percentage={percentage}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: 'none', md: 'block' }, mx: 2, borderColor: '#EDF2F7' }}
          />

          <Box sx={{ flex: 1, pl: { xs: 0, md: 2 }, height: '250px', width: '100%' }}>
            <SatisfactionBarChart data={chartData.series} loading={loading} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CaptivePortalAccessSatisfactionScore;
